import type { Board } from './board';
import type { Pos, Special, TileKind } from './types';
import { posKey, samePos } from './types';

export interface MatchGroup {
  kind: TileKind;
  cells: Pos[];
  /** special piece this match creates, and where */
  create: { special: Special; at: Pos } | null;
}

interface Run {
  dir: 'h' | 'v';
  cells: Pos[];
}

/** A player swap: `to` is where the dragged piece ended up. */
export interface SwapInfo {
  from: Pos;
  to: Pos;
}

function findRuns(board: Board): Run[] {
  const runs: Run[] = [];
  const scan = (outer: number, inner: number, dir: 'h' | 'v') => {
    for (let o = 0; o < outer; o++) {
      let start = 0;
      for (let i = 1; i <= inner; i++) {
        const at = (k: number) => (dir === 'h' ? board.kindAt(o, k) : board.kindAt(k, o));
        const kind = at(start);
        if (i < inner && kind && at(i) === kind) continue;
        if (kind && i - start >= 3) {
          const cells: Pos[] = [];
          for (let k = start; k < i; k++) cells.push(dir === 'h' ? { row: o, col: k } : { row: k, col: o });
          runs.push({ dir, cells });
        }
        start = i;
      }
    }
  };
  scan(board.rows, board.cols, 'h');
  scan(board.cols, board.rows, 'v');
  return runs;
}

/**
 * Finds all matches and decides which special each one creates:
 *   5 in a line          -> colorstar
 *   L / T / + shape      -> bomb
 *   4 in a line          -> stripe (direction: see below)
 * Stripe direction: after a player swap it follows the swipe (horizontal
 * swipe -> clears the row); in cascades it runs across the match line.
 * The special appears where the player dropped the piece, otherwise at the
 * crossing of an L/T or near the middle of the line.
 */
export function findMatches(board: Board, swap: SwapInfo | null = null): MatchGroup[] {
  const runs = findRuns(board);
  // union runs that share a cell (L/T/+ shapes)
  const parent = runs.map((_, i) => i);
  const root = (i: number): number => (parent[i] === i ? i : (parent[i] = root(parent[i])));
  const owner = new Map<number, number>();
  runs.forEach((run, i) =>
    run.cells.forEach((p) => {
      const k = posKey(p);
      const other = owner.get(k);
      if (other === undefined) owner.set(k, i);
      else parent[root(i)] = root(other);
    }),
  );
  const groups = new Map<number, Run[]>();
  runs.forEach((run, i) => {
    const r = root(i);
    groups.set(r, [...(groups.get(r) ?? []), run]);
  });

  const out: MatchGroup[] = [];
  for (const members of groups.values()) {
    const cellMap = new Map<number, Pos>();
    for (const run of members) for (const p of run.cells) cellMap.set(posKey(p), p);
    const cells = [...cellMap.values()];
    const kind = board.kindAt(cells[0].row, cells[0].col)!;
    const longest = members.reduce((a, b) => (b.cells.length > a.cells.length ? b : a));
    const hasH = members.some((r) => r.dir === 'h');
    const hasV = members.some((r) => r.dir === 'v');

    let special: Special | null = null;
    if (longest.cells.length >= 5) special = 'colorstar';
    else if (hasH && hasV) special = 'bomb';
    else if (longest.cells.length === 4) {
      if (swap) special = swap.from.row === swap.to.row ? 'stripeH' : 'stripeV';
      else special = longest.dir === 'h' ? 'stripeV' : 'stripeH';
    }

    let at: Pos | null = null;
    if (special) {
      if (swap) at = cells.find((p) => samePos(p, swap.to)) ?? cells.find((p) => samePos(p, swap.from)) ?? null;
      if (!at && special === 'bomb') {
        const counts = new Map<number, number>();
        for (const run of members) for (const p of run.cells) counts.set(posKey(p), (counts.get(posKey(p)) ?? 0) + 1);
        at = cells.find((p) => (counts.get(posKey(p)) ?? 0) > 1) ?? null;
      }
      if (!at) at = longest.cells[Math.floor((longest.cells.length - 1) / 2)];
    }
    out.push({ kind, cells, create: special && at ? { special, at } : null });
  }
  return out;
}

/** Would swapping a and b create a match? (Only checks lines through a and b.) */
export function swapMakesMatch(board: Board, a: Pos, b: Pos): boolean {
  const ca = board.at(a);
  const cb = board.at(b);
  [ca.piece, cb.piece] = [cb.piece, ca.piece];
  const hit = lineAt(board, a) || lineAt(board, b);
  [ca.piece, cb.piece] = [cb.piece, ca.piece];
  return hit;
}

function lineAt(board: Board, p: Pos): boolean {
  const k = board.kindAt(p.row, p.col);
  if (!k) return false;
  const len = (dr: number, dc: number) => {
    let n = 0;
    while (board.kindAt(p.row + dr * (n + 1), p.col + dc * (n + 1)) === k) n++;
    return n;
  };
  return len(0, -1) + len(0, 1) >= 2 || len(-1, 0) + len(1, 0) >= 2;
}
